import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { patientsApi, scansApi, womensHealthApi } from '../../lib/api';
import {
  Users, Image, Plus, ArrowRight, TrendingUp, Sparkles, Clock,
  Baby, Calendar, HeartPulse, ChevronRight, Activity, AlertTriangle
} from 'lucide-react';
import { OnboardingModal } from '../../components/shared/OnboardingModal';
import { CreditPanel } from '../../components/credits/CreditPanel';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const weeklyData = [
  { name: 'Pzt', scans: 3 },
  { name: 'Sal', scans: 5 },
  { name: 'Car', scans: 4 },
  { name: 'Per', scans: 7 },
  { name: 'Cum', scans: 6 },
  { name: 'Cmt', scans: 2 },
  { name: 'Paz', scans: 0 },
];

export const DoctorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ totalPatients: 0, totalScans: 0, pendingScans: 0, generatedScans: 0 });
  const [whStats, setWhStats] = useState({ total: 0, awaiting: 0 });
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const today = new Date().toLocaleDateString('tr-TR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const firstName = user?.name?.split(' ')[0] || 'Doktor';

  useEffect(() => {
    const fetchData = async () => {
      // Each card fetches independently — one failure cannot break the others
      try {
        const patientsData = await patientsApi.list({ limit: 5 });
        setStats(prev => ({ ...prev, totalPatients: patientsData.total }));
        setRecentPatients(patientsData.patients.slice(0, 5));
      } catch (e) {
        console.warn('[Dashboard] patients fetch failed:', e?.message);
      }

      try {
        const scansData = await scansApi.list({ limit: 100 });
        setStats(prev => ({
          ...prev,
          totalScans: scansData.total,
          pendingScans: scansData.scans.filter(s => s.status === 'pending').length,
          generatedScans: scansData.scans.filter(s => s.status === 'generated').length,
        }));
      } catch (e) {
        console.warn('[Dashboard] scans fetch failed:', e?.message);
      }

      try {
        const whData = await womensHealthApi.listEncounters({ limit: 5 });
        setWhStats({
          total: whData.total || 0,
          awaiting: (whData.encounters || []).filter(e => e.status === 'awaiting_review').length,
        });
      } catch {}

      setLoading(false);
      
      // Show onboarding for new users
      if (user && user.onboarding_completed === false) {
        setShowOnboarding(true);
      }
    };
    fetchData();
  }, [user]);

  return (
    <DashboardLayout title={`Merhaba, Dr. ${firstName}`} subtitle={today}>
      <OnboardingModal open={showOnboarding} onClose={() => setShowOnboarding(false)} />
      <div className="space-y-6" data-testid="doctor-dashboard">

        {/* Step 8: Credit balance + free 4K + redeem + packages */}
        <CreditPanel variant="doctor" />

        {/* Stat Cards - 4 col grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer" onClick={() => navigate('/doctor/patients')}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Toplam Hasta</p>
                  <p className="text-3xl font-light tracking-tight text-gray-900 mt-1" data-testid="stat-total-patients">
                    {loading ? '-' : stats.totalPatients}
                  </p>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-emerald-500" strokeWidth={1.5} /> Anne adaylari
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#5A67D8]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#5A67D8]" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Toplam Tarama</p>
                  <p className="text-3xl font-light tracking-tight text-gray-900 mt-1" data-testid="stat-total-scans">
                    {loading ? '-' : stats.totalScans}
                  </p>
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <Image className="w-3 h-3" strokeWidth={1.5} /> Ultrason kayitlari
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                  <Image className="w-5 h-5 text-violet-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>AI Gorseller</p>
                  <p className="text-3xl font-light tracking-tight text-gray-900 mt-1" data-testid="stat-generated">
                    {loading ? '-' : stats.generatedScans}
                  </p>
                  <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} /> AI ile uretildi
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Bekleyen</p>
                  <p className="text-3xl font-light tracking-tight text-gray-900 mt-1" data-testid="stat-pending">
                    {loading ? '-' : stats.pendingScans}
                  </p>
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" strokeWidth={1.5} /> Islem bekliyor
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart + Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Chart - 3 cols */}
          <Card className="lg:col-span-3 bg-white border border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Calendar className="w-4 h-4 text-[#5A67D8]" strokeWidth={1.5} />
                Haftalik Tarama Trendi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={weeklyData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorScans" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#5A67D8" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#5A67D8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                    <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} />
                    <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff', border: '1px solid #E5E7EB',
                        borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '13px',
                      }}
                    />
                    <Area type="monotone" dataKey="scans" stroke="#5A67D8" strokeWidth={2} fillOpacity={1} fill="url(#colorScans)" name="Tarama" dot={{ r: 3, fill: '#5A67D8', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#5A67D8', strokeWidth: 2, stroke: '#fff' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions - 1 col */}
          <Card className="bg-white border border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Hizli Islemler</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start h-11 bg-[#5A67D8] hover:bg-[#4C51BF] text-sm font-medium" onClick={() => navigate('/doctor/patients')} data-testid="add-patient-btn">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Hasta Ekle
              </Button>
              <Button variant="outline" className="w-full justify-between h-11 text-sm border-gray-200 text-gray-700 hover:bg-gray-50" onClick={() => navigate('/doctor/patients')} data-testid="quick-patients">
                <span className="flex items-center gap-2"><Users className="w-4 h-4" strokeWidth={1.5} /> Hastalarim</span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Button>
              <Button variant="outline" className="w-full justify-between h-11 text-sm border-gray-200 text-gray-700 hover:bg-gray-50" onClick={() => navigate('/doctor/womens-health')} data-testid="quick-wh">
                <span className="flex items-center gap-2"><HeartPulse className="w-4 h-4" strokeWidth={1.5} /> Kadin Sagligi</span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </Button>
              {whStats.awaiting > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span>{whStats.awaiting} vaka degerlendirme bekliyor</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Patients - table style */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Activity className="w-4 h-4 text-[#5A67D8]" strokeWidth={1.5} />
              Son Hastalar
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/doctor/patients')} className="text-[#5A67D8] text-xs hover:bg-[#5A67D8]/5" data-testid="view-all-patients">
              Tumunu Gor <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-gray-400 text-sm">Yukleniyor...</div>
            ) : recentPatients.length === 0 ? (
              <div className="text-center py-12">
                <Baby className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-400 mb-3">Henuz hasta kaydi yok</p>
                <Button onClick={() => navigate('/doctor/patients')} size="sm" className="bg-[#5A67D8] hover:bg-[#4C51BF]" data-testid="first-patient-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Ilk Hastayi Ekle
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-500" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Hasta</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 hidden sm:table-cell" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Hafta</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 hidden md:table-cell" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Telefon</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {recentPatients.map((patient) => (
                      <tr key={patient.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/doctor/patients/${patient.id}`)}>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#5A67D8]/10 flex items-center justify-center flex-shrink-0">
                              <Baby className="w-4 h-4 text-[#5A67D8]" strokeWidth={1.5} />
                            </div>
                            <span className="text-sm font-medium text-gray-800">{patient.first_name} {patient.last_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 hidden sm:table-cell">
                          {patient.pregnancy_week ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#5A67D8]/10 text-[#5A67D8]">
                              {patient.pregnancy_week}. hafta
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-sm text-gray-500 hidden md:table-cell">{patient.phone || '-'}</td>
                        <td className="px-6 py-3.5 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-300 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DoctorDashboard;
